import { useState } from "react";
import { Plus, Trash2, GripVertical, Upload, X, Link as LinkIcon } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

/**
 * Block field components. Each takes:
 *   { block: {...}, value, onChange(newValue) }
 * where `value` is the working DRAFT value bound to the editor form.
 */

function resolveImage(img) {
  if (!img) return "";
  const url = typeof img === "string" ? img : img.url;
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("/")) return url;
  return `${BACKEND_URL}/api/cms/files/${url}`;
}

export function TextField({ block, value, onChange }) {
  return (
    <input
      className="input-clay h-11 px-3 w-full"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`field-${block.key}`}
    />
  );
}

export function TextAreaField({ block, value, onChange }) {
  return (
    <textarea
      rows={4}
      className="input-clay px-3 py-2 w-full font-mono text-sm"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`field-${block.key}`}
    />
  );
}

export function LinkField({ block, value, onChange }) {
  const v = typeof value === "object" && value ? value : { label: "", href: "" };
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <div>
        <div className="text-xs text-slate-500 mb-1">Button label</div>
        <input
          className="input-clay h-10 px-3 w-full"
          placeholder="Click me"
          value={v.label || ""}
          onChange={(e) => onChange({ ...v, label: e.target.value })}
          data-testid={`field-${block.key}-label`}
        />
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><LinkIcon size={12}/> Destination URL</div>
        <input
          className="input-clay h-10 px-3 w-full"
          placeholder="/schedule or https://..."
          value={v.href || ""}
          onChange={(e) => onChange({ ...v, href: e.target.value })}
          data-testid={`field-${block.key}-href`}
        />
      </div>
    </div>
  );
}

export function ListField({ block, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const setAt = (i, v) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...items];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <button type="button" onClick={() => moveUp(i)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Move up" data-testid={`list-${block.key}-up-${i}`}>
            <GripVertical size={14}/>
          </button>
          <input
            className="input-clay h-10 px-3 flex-1"
            value={it || ""}
            onChange={(e) => setAt(i, e.target.value)}
            data-testid={`list-${block.key}-item-${i}`}
          />
          <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Remove" data-testid={`list-${block.key}-remove-${i}`}>
            <Trash2 size={14}/>
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs font-semibold text-green-700 hover:text-green-900 inline-flex items-center gap-1" data-testid={`list-${block.key}-add`}>
        <Plus size={12}/> Add item
      </button>
    </div>
  );
}

export function StepsField({ block, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const setAt = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { title: "", body: "" }]);
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...items];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border-2 border-slate-100 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500">#{i + 1}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => moveUp(i)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`steps-${block.key}-up-${i}`}><GripVertical size={12}/></button>
              <button type="button" onClick={() => remove(i)} className="p-1 rounded-lg text-red-500 hover:bg-red-50" data-testid={`steps-${block.key}-remove-${i}`}><Trash2 size={12}/></button>
            </div>
          </div>
          <input
            className="input-clay h-9 px-3 w-full text-sm font-semibold mb-2"
            placeholder="Title"
            value={it.title || ""}
            onChange={(e) => setAt(i, { title: e.target.value })}
            data-testid={`steps-${block.key}-title-${i}`}
          />
          <textarea
            rows={2}
            className="input-clay px-3 py-2 w-full text-sm"
            placeholder="Body"
            value={it.body || ""}
            onChange={(e) => setAt(i, { body: e.target.value })}
            data-testid={`steps-${block.key}-body-${i}`}
          />
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs font-semibold text-green-700 hover:text-green-900 inline-flex items-center gap-1" data-testid={`steps-${block.key}-add`}>
        <Plus size={12}/> Add step
      </button>
    </div>
  );
}

export function FaqField({ block, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const setAt = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { title: "", bullets: [""] }]);
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...items];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const setBullet = (i, bi, v) => {
    const bullets = [...(items[i].bullets || [])];
    bullets[bi] = v;
    setAt(i, { bullets });
  };
  const addBullet = (i) => setAt(i, { bullets: [...(items[i].bullets || []), ""] });
  const removeBullet = (i, bi) => setAt(i, { bullets: (items[i].bullets || []).filter((_, idx) => idx !== bi) });

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border-2 border-slate-100 p-3 bg-slate-50/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500">Entry #{i + 1}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => moveUp(i)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`faq-${block.key}-up-${i}`}><GripVertical size={12}/></button>
              <button type="button" onClick={() => remove(i)} className="p-1 rounded-lg text-red-500 hover:bg-red-50" data-testid={`faq-${block.key}-remove-${i}`}><Trash2 size={12}/></button>
            </div>
          </div>
          <input
            className="input-clay h-9 px-3 w-full text-sm font-semibold mb-2"
            placeholder="Question / section title"
            value={it.title || ""}
            onChange={(e) => setAt(i, { title: e.target.value })}
            data-testid={`faq-${block.key}-title-${i}`}
          />
          <div className="space-y-1">
            {(it.bullets || []).map((b, bi) => (
              <div key={bi} className="flex items-start gap-1">
                <textarea
                  rows={2}
                  className="input-clay px-2 py-1 flex-1 text-sm"
                  placeholder="Bullet / answer line"
                  value={b || ""}
                  onChange={(e) => setBullet(i, bi, e.target.value)}
                  data-testid={`faq-${block.key}-bullet-${i}-${bi}`}
                />
                <button type="button" onClick={() => removeBullet(i, bi)} className="p-1 rounded-lg text-red-400 hover:bg-red-50 mt-1"><X size={12}/></button>
              </div>
            ))}
            <button type="button" onClick={() => addBullet(i)} className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <Plus size={10}/> Add bullet
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs font-semibold text-green-700 hover:text-green-900 inline-flex items-center gap-1" data-testid={`faq-${block.key}-add`}>
        <Plus size={12}/> Add entry
      </button>
    </div>
  );
}

export function ImageField({ block, value, onChange, uploadFn }) {
  const v = typeof value === "object" && value ? value : { url: "", alt: "" };
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const media = await uploadFn(file, v.alt || "");
      onChange({ ...v, url: media.storage_path });
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const preview = resolveImage(v);

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        {preview ? (
          <img src={preview} alt={v.alt || ""} className="h-16 w-16 rounded-xl object-cover bg-slate-100 border-2 border-slate-200"/>
        ) : (
          <div className="h-16 w-16 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs">no image</div>
        )}
        <label className={`btn-push-outline px-3 py-2 text-xs cursor-pointer inline-flex items-center gap-1 ${uploading ? "opacity-60" : ""}`} data-testid={`image-${block.key}-upload`}>
          <Upload size={12}/>
          {uploading ? "Uploading…" : "Replace image"}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading}/>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <div className="text-xs text-slate-500 mb-1">Or paste a URL</div>
          <input
            className="input-clay h-9 px-3 w-full text-sm"
            placeholder="/logo.png or https://..."
            value={v.url || ""}
            onChange={(e) => onChange({ ...v, url: e.target.value })}
            data-testid={`image-${block.key}-url`}
          />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Alt text (for screen readers)</div>
          <input
            className="input-clay h-9 px-3 w-full text-sm"
            value={v.alt || ""}
            onChange={(e) => onChange({ ...v, alt: e.target.value })}
            data-testid={`image-${block.key}-alt`}
          />
        </div>
      </div>
    </div>
  );
}

export const FIELD_TYPES = {
  text: TextField,
  textarea: TextAreaField,
  link: LinkField,
  list: ListField,
  steps: StepsField,
  faq: FaqField,
  image: ImageField,
};
